import { GeocoordinateSimpleValueGISSchema } from "@validereinc/utilities";
import z, { type UnknownKeysParam } from "zod";
import type { TypeOfObjectValues } from "./types.ts";

/**
 * The unique identifier for anything in the domain.
 * Currently a UUID.
 */
export const IdentifierSchema = z.string().uuid();
export type IdentifierType = z.infer<typeof IdentifierSchema>;

export const DomainModelSchema = z.object({
  /** the unique identifier of this model/model in the database */
  id: IdentifierSchema.readonly(),
});
export type DomainModelType = z.infer<typeof DomainModelSchema>;

export const DomainModelReferenceSchema = DomainModelSchema.merge(
  z.object({
    /** the name of the model */
    name: z.string(),
  })
);
export type DomainModelReferenceType = z.infer<
  typeof DomainModelReferenceSchema
>;

export const DomainModelMetaSchema = z.object({
  /** when was this model created? (will be an ISO UTC date string) */
  created_at: z.string().datetime(),
  /** when was this model updated? (will be an ISO UTC date string) */
  updated_at: z.string().datetime(),
});
export type DomainModelMetaType = z.infer<typeof DomainModelMetaSchema>;

export const DomainModelMetaExtendedSchema = DomainModelMetaSchema.merge(
  z.object({
    /** who created this model? (will be a UUID of a user model) */
    created_by: z.string().uuid().nullable(),
    /** who updated this model? (will be a UUID of a user model) */
    updated_by: z.string().uuid().nullable(),
  })
);
export type DomainModelMetaExtendedType = z.infer<
  typeof DomainModelMetaExtendedSchema
>;

export const SortDirection = {
  ASCENDING: "asc",
  DESCENDING: "desc",
} as const;
export type SortDirectionType =
  (typeof SortDirection)[keyof typeof SortDirection];

export const PaginationParamsSchema = z.object({
  /** the page of data to retrieve */
  page: z.number().min(1),
  /** the number of entries per page to retrieve */
  pageSize: z.number().min(0),
  /** whether to disable the total count of the data */
  pageDisableTotal: z.boolean().optional(),
});
export type PaginationParamsType = z.infer<typeof PaginationParamsSchema>;

export const PaginationCursorParamsSchema = z.object({
  /* the deterministic property on the data being paginated is used as a "cursor" - a starting point to count from */
  cursorToken: z.string().optional(),
  /** the number of entries per page to retrieve */
  pageSize: z.number().min(0),
});
export type PaginationCursorParamsType = z.infer<
  typeof PaginationCursorParamsSchema
>;

export const SortParamsSchema = z.object({
  /** the field name to sort on */
  sortBy: z.string(),
  /** the direction to sort the field by */
  sortDirection: z.nativeEnum(SortDirection),
  /** the reference location for distance sorting. [Longitude, Latitude] */
  referenceLocation: GeocoordinateSimpleValueGISSchema.optional(),
});
export type SortParamsType = z.infer<typeof SortParamsSchema>;

export const GroupParamsSchema = z.object({
  /** the field of a particular model to group by */
  groupBy: z.array(z.string()),
});
export type GroupParamsType = z.infer<typeof GroupParamsSchema>;

export const PaginationAndSortParamsSchema = PaginationParamsSchema.merge(
  SortParamsSchema.partial()
);
export type PaginationAndSortParamsType = z.infer<
  typeof PaginationAndSortParamsSchema
>;

export const PaginationCursorAndSortParamsSchema =
  PaginationCursorParamsSchema.merge(SortParamsSchema);
export type PaginationCursorAndSortParamsType = z.infer<
  typeof PaginationCursorAndSortParamsSchema
>;

export const PaginationSortAndGroupParamsSchema = PaginationParamsSchema.merge(
  SortParamsSchema
)
  .merge(GroupParamsSchema)
  .partial();
export type PaginationSortAndGroupParamsType = z.infer<
  typeof PaginationSortAndGroupParamsSchema
>;

export const ResponseErrorSchema = z.object({
  /** was the original request this response was for, successful? */
  success: z.boolean(),
  /** a description of the error from the API */
  error: z
    .string()
    .optional()
    .describe("A description of the error from the API. Might be technical."),
});
export type ResponseErrorType = z.infer<typeof ResponseErrorSchema>;

export const ResponseSuccessSchema = z.object({
  /** was the original request this response was for, successful? */
  success: z.boolean(),
  /** information about the success or failure of the request */
  message: z.string(),
});
export type ResponseSuccessType = z.infer<typeof ResponseSuccessSchema>;

export const isResponseErrorType = (
  data: unknown
): data is ResponseErrorType => {
  return ResponseErrorSchema.safeParse(data).success;
};

/**
 * Creates a schema for a grouped by entry.
 * @example grouping by name first, then country
 * ```js
 * const GroupByStatusAndCountryEntrySchema = createGroupedByEntrySchema(z.enum(["active", "inactive"]), z.object({ country: z.string() }));
 * const GroupByStatusAndCountryDataSchema = createGetListResponseSchema(GroupByStatusAndCountryEntrySchema);
 *
 * GroupByStatusAndCountryDataSchema.parse({
 *   data: [
 *     { value: "active", country: "USA", count: 50 },
 *     { value: "inactive", country: "Canada", count: 2 },
 *   ],
 *   page_number: 1,
 *   page_size: 10,
 *   total_entries: 52,
 *   total_pages: 6,
 *   ],
 * });
 * ```
 * @param valueSchema - the value schema for the value of the first group by field
 * @param additionalGroupByFieldsSchema - the key+value object schema representing additional group by fields
 * @returns schema for a grouped by entry
 */
export const createGroupedByEntrySchema = <T extends z.ZodRawShape>(
  valueSchema: z.ZodTypeAny,
  additionalGroupByFieldsSchema?: z.ZodObject<T>
) => {
  if (additionalGroupByFieldsSchema) {
    return z
      .object({
        value: valueSchema,
        count: z.number().min(0),
      })
      .merge(additionalGroupByFieldsSchema);
  }

  return z.object({
    value: valueSchema,
    count: z.number().min(0),
  });
};

export type GroupedByEntryType<
  T = unknown,
  U extends object = Record<string, never>,
> = {
  value: T;
  count: number;
} & U;

export const createGetListRequestSchema = <
  TFilter extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  filterSchema: TFilter,
  metaSchema?: TMeta
) =>
  z
    .object({
      /** the filters to apply to search for a list of entries */
      filters: filterSchema.optional(),
      /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
      meta: metaSchema?.optional() ?? z.never(),
    })
    .merge(PaginationSortAndGroupParamsSchema);
export interface GetListRequestType<TFilter = any, TMeta = any>
  extends PaginationSortAndGroupParamsType {
  /** the filters to apply to search for a list of entries */
  filters?: TFilter;
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export const createExportListRequestSchema = <
  TFilter extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  filterSchema: TFilter,
  metaSchema?: TMeta
) =>
  z
    .object({
      /** the filters to apply to search for a list of entries */
      filters: filterSchema.optional(),
      /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
      meta: metaSchema?.optional() ?? z.never(),
    })
    .merge(PaginationSortAndGroupParamsSchema);

export const createGetListResponseSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
>(
  modelSchema: TDomainModel
) =>
  z.object({
    /** list of entries that satisfy the provided filters */
    data: z.array(modelSchema),
    /** the page number of entries requested */
    page_number: z.number().min(1),
    /** the page size of entries being used to determine the # of pages */
    page_size: z.number().min(0),
    /** the total number of entries available */
    total_entries: z.number().min(0),
    /** the total number of pages based on the page_size available */
    total_pages: z.number().min(0),
  });
export interface GetListResponseType<TDomainModel> {
  /** list of entries that satisfy the provided filters */
  data: TDomainModel[];
  /** the page number of entries requested */
  page_number: number;
  /** the page size of entries being used to determine the # of pages */
  page_size: number;
  /** the total number of entries available */
  total_entries: number;
  /** the total number of pages based on the page_size available */
  total_pages: number;
}

export const createGetOneRequestSchema = <
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  metaSchema?: TMeta
) =>
  z.object({
    /** the ID of the model you want to get */
    id: IdentifierSchema,
    /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
    meta: metaSchema?.optional() ?? z.never(),
  });
export interface GetOneRequestType<TMeta = any> {
  /** the ID of the model you want to get */
  id: IdentifierType;
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export const createCursorListResponseResponseSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
>(
  modelSchema: TDomainModel
) =>
  z.object({
    /** the list of entries */
    data: z.array(modelSchema),
    /** the total number of entries that exist */
    total_entries: z.number(),
    /** pointer to the next cursor token */
    next_cursor_token: z.string().nullable(),
  });
export interface CursorListResponseType<TDomainModel> {
  /** the list of entries */
  data: TDomainModel[];
  /** the total number of entries that exist */
  total_entries: number;
  /** pointer to the next cursor token */
  next_cursor_token?: string | null;
}

export const createGetOneResponseSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
>(
  modelSchema: TDomainModel
) =>
  z.object({
    /** the model requested */
    data: modelSchema,
  });
export interface GetOneResponseType<TDomainModel> {
  /** the model requested */
  data: TDomainModel;
}

export const createGetManyRequestSchema = <
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  metaSchema?: TMeta
) =>
  z.object({
    /** list of IDs corresponding to entries you want to get */
    ids: z.array(IdentifierSchema),
    /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
    meta: metaSchema?.optional() ?? z.never(),
  });
export interface GetManyRequestType<TMeta = any> {
  /** list of IDs corresponding to entries you want to get */
  ids: IdentifierType[];
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export const createGetManyResponseSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
>(
  modelSchema: TDomainModel
) =>
  z.object({
    /** list of entries requested */
    data: z.array(modelSchema),
  });
export interface GetManyResponseType<TDomainModel> {
  /** list of entries requested */
  data: TDomainModel[];
}

export const createCreateOneRequestSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  modelSchema: TDomainModel,
  metaSchema?: TMeta
) =>
  z.object({
    /** create payload to send */
    data: modelSchema,
    /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
    meta: metaSchema?.optional() ?? z.never(),
  });
export interface CreateOneRequestType<TData = any, TMeta = any> {
  /** create payload to send */
  data: TData;
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export const createCreateOneResponseSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
>(
  modelSchema: TDomainModel
) =>
  z.object({
    /** the created model */
    data: modelSchema,
  });
export interface CreateOneResponseType<TDomainModel> {
  /** the created model */
  data: TDomainModel;
}

export const createCreateManyRequestSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  modelSchema: TDomainModel,
  metaSchema?: TMeta
) =>
  z.object({
    /** the entries to create */
    data: z.array(modelSchema),
    /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
    meta: metaSchema?.optional() ?? z.never(),
  });
export interface CreateManyRequestType<TData = any, TMeta = any> {
  /** the entries to create */
  data: TData[];
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export const createUpdateOneRequestSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  modelSchema: TDomainModel,
  metaSchema?: TMeta
) =>
  z.object({
    /** the ID of the model to update */
    id: IdentifierSchema,
    /** the data of the model to update */
    data: modelSchema,
    /** the previous state of the model before changes */
    previousData: modelSchema.optional(),
    /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
    meta: metaSchema?.optional() ?? z.never(),
  });
export interface UpdateOneRequestType<TDomainModel, TMeta = any> {
  /** the ID of the model to update */
  id: IdentifierType;
  /** the data of the model to update */
  data: Partial<TDomainModel>;
  /** the previous state of the model before changes */
  previousData?: TDomainModel;
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export const createUpdateOneResponseSchema = <
  TDomainModel extends z.ZodTypeAny,
>(
  modelSchema: TDomainModel
) =>
  z.object({
    /** the updated model */
    data: modelSchema,
  });
export interface UpdateOneResponseType<TDomainModel> {
  /** the updated model */
  data: TDomainModel;
}

export const createUpdateManyRequestSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  modelSchema: TDomainModel,
  metaSchema?: TMeta
) =>
  z.object({
    /** the IDs of entries to update */
    ids: z.array(IdentifierSchema),
    /** the data to update for all entries */
    data: modelSchema,
    /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
    meta: metaSchema?.optional() ?? z.never(),
  });
export interface UpdateManyRequestType<TData = any, TMeta = any> {
  /** the IDs of entries to update */
  ids: IdentifierType[];
  /** the data to update for all entries */
  data: TData;
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export const createDeleteOneRequestSchema = <
  TDomainModel extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  modelSchema: TDomainModel,
  metaSchema?: TMeta
) =>
  z.object({
    /** the ID of the model to update */
    id: IdentifierSchema,
    /** the previous state of the model before changes */
    previousData: modelSchema.optional(),
    /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
    meta: metaSchema?.optional() ?? z.never(),
  });
export interface DeleteOneRequestType<TDomainModel, TMeta = any> {
  /** the ID of the model to delete */
  id: IdentifierType;
  /** the previous state of the model */
  previousData?: TDomainModel;
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export type DeleteOneResponseType = ResponseSuccessType | ResponseErrorType;

export const createDeleteManyRequestSchema = <
  TMeta extends z.ZodTypeAny = z.ZodNever,
>(
  metaSchema?: TMeta
) =>
  z.object({
    /** the IDs of entries to delete */
    ids: z.array(IdentifierSchema),
    /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
    meta: metaSchema?.optional() ?? z.never(),
  });
export interface DeleteManyRequestType<TMeta = any> {
  /** the IDs of the entries to delete */
  ids: IdentifierType[];
  /** additional metadata that was passed in. typing is generic - check the implementor for actual type.  */
  meta?: TMeta;
}

export const BulkActionResponseStatus = {
  SUCCESS: "success",
  FAILED: "failed",
  SKIPPED: "skipped",
  STARTED: "started",
} as const;
export type BulkActionResponseStatusType = TypeOfObjectValues<
  typeof BulkActionResponseStatus
>;

export const createBulkActionResponseType = <TData extends z.ZodTypeAny>(
  dataSchema?: TData
) =>
  z.object({
    /** Whether the whole job succeeded or failed */
    status: z.nativeEnum(BulkActionResponseStatus),
    /** total # of entries that succeeded */
    success_count: z.number().min(0).optional(),
    /** total # of entries that errored */
    error_count: z.number().min(0).optional(),
    /** A record with a key for each result in the bulk action */
    data: z.record(
      IdentifierSchema,
      z.object({
        /** Whether the individual result succeeded or failed */
        status: z.nativeEnum(BulkActionResponseStatus),
        /** If an error occurred, what that error was */
        error: z.string().optional(),
        /** If a result has data, it is returned here */
        data: dataSchema ?? z.never(),
      })
    ),
  });
/** When multiple entries of one model are mutated at the same time, the
 * response should try to conform to this shape */
export type BulkActionResponseType<T> = {
  /** Whether the whole job succeeded or failed */
  status: BulkActionResponseStatusType;
  /** A record with a key for each result in the bulk action */
  data: Record<
    string,
    {
      /** Whether the individual result succeeded or failed */
      status: BulkActionResponseStatusType;
      /** If an error occurred, what that error was */
      error?: string;
      /** If a result has data, it is returned here */
      data?: T;
    }
  >;
};

/**
 * @deprecated use GetListRequestType instead
 */
export type PaginatedListRequestType<T> = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: SortDirectionType;
  groupBy?: string[];
  filters?: T;
};
